import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  MoreHorizontal,
  RefreshCw,
  Pencil,
  Trash2,
  Bell,
  TrendingUp,
  AlertTriangle,
  Maximize2,
  Minimize2,
  History,
  GripVertical,
  Sliders,
} from "lucide-react";
import ChartRenderer from "./ChartRenderer";
import { api } from "../../lib/api";

export default function ChartCard({
  chart,
  onEdit,
  onDelete,
  onAddAlarm,
  onShowEvents,
  onChartUpdate,
  canEdit = true,
  canConfigure = true,
  inGrid = false,
  /** Active dashboard time range { from, to } — synced across all charts. */
  range = null,
  /** Bumped by parent to force a reload (e.g. "Refresh all"). */
  refreshKey = 0,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [width, setWidth] = useState(chart.width || 6);
  const [height, setHeight] = useState(chart.height || 280);
  const containerRef = useRef(null);
  const [measuredHeight, setMeasuredHeight] = useState(220);

  // Local Y-axis settings — initialized from chart record, synced on save.
  const [yMode, setYMode] = useState(chart.y_mode || "auto");
  const [yMin, setYMin] = useState(
    chart.y_min !== null && chart.y_min !== undefined ? String(chart.y_min) : ""
  );
  const [yMax, setYMax] = useState(
    chart.y_max !== null && chart.y_max !== undefined ? String(chart.y_max) : ""
  );
  const [yPopoverOpen, setYPopoverOpen] = useState(false);
  const [savingY, setSavingY] = useState(false);

  useEffect(() => {
    setYMode(chart.y_mode || "auto");
    setYMin(chart.y_min !== null && chart.y_min !== undefined ? String(chart.y_min) : "");
    setYMax(chart.y_max !== null && chart.y_max !== undefined ? String(chart.y_max) : "");
  }, [chart.y_mode, chart.y_min, chart.y_max]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (range?.from) params.from = range.from;
      if (range?.to) params.to = range.to;
      const { data } = await api.get(`/charts/${chart.id}/data`, { params });
      if (data.error) {
        setError(data.error);
        setRows([]);
      } else {
        setRows(data.rows || []);
      }
      setAlarms(data.alarms || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [chart.id, range?.from, range?.to]);

  // Reload when chart, range, or external refresh trigger changes.
  // Auto-refresh interval intentionally disabled — user controls refresh manually.
  useEffect(() => {
    load();
  }, [load, chart.sql_query, chart.datasource_id, refreshKey]);

  // In-grid: measure container height continuously so Recharts can fill
  useEffect(() => {
    if (!inGrid || !containerRef.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver((entries) => {
      for (const ent of entries) {
        const h = ent.contentRect.height;
        if (h > 0) setMeasuredHeight(Math.round(h));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [inGrid]);

  async function persistSize(nextWidth, nextHeight) {
    try {
      await api.patch(`/charts/${chart.id}`, { width: nextWidth, height: nextHeight });
    } catch (err) {
      console.warn("chart resize persist failed:", err?.message);
    }
  }
  function bump(delta) {
    const next = Math.max(3, Math.min(12, width + delta));
    setWidth(next);
    persistSize(next, height);
  }
  function bumpHeight(delta) {
    const next = Math.max(180, Math.min(560, height + delta));
    setHeight(next);
    persistSize(width, next);
  }

  async function saveYSettings() {
    setSavingY(true);
    try {
      const payload = {
        y_mode: yMode,
        y_min: yMode === "fixed" && yMin !== "" ? Number(yMin) : null,
        y_max: yMode === "fixed" && yMax !== "" ? Number(yMax) : null,
      };
      await api.patch(`/charts/${chart.id}`, payload);
      onChartUpdate?.(chart.id, payload);
      setYPopoverOpen(false);
    } catch (err) {
      console.warn("y-axis save failed:", err?.message);
    } finally {
      setSavingY(false);
    }
  }

  const triggered = alarms.some((a) => a.triggered);
  const renderHeight = inGrid ? Math.max(80, measuredHeight - 8) : height;

  const wrapperClass = useMemo(() => {
    if (inGrid) return "h-full w-full";
    const w = Math.max(3, Math.min(12, width));
    return `col-span-12 md:col-span-12 xl:col-span-${w}`;
  }, [inGrid, width]);

  // Numeric versions of yMin/yMax for the renderer
  const yMinNum = yMin === "" ? null : Number(yMin);
  const yMaxNum = yMax === "" ? null : Number(yMax);

  return (
    <div className={wrapperClass} data-testid={`chart-card-${chart.id}`}>
      <Card className="card-soft h-full flex flex-col overflow-hidden">
        <CardHeader
          className={`pb-2 flex-row items-start justify-between space-y-0 shrink-0 ${
            inGrid ? "chart-drag-handle select-none" : ""
          }`}
        >
          <div className="min-w-0 flex items-start gap-2">
            {inGrid && (
              <GripVertical className="h-3.5 w-3.5 text-muted-foreground/60 mt-0.5 shrink-0" />
            )}
            <div className="min-w-0">
              <CardTitle className="text-sm font-semibold truncate flex items-center gap-2">
                <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
                {chart.title}
              </CardTitle>
              <div className="flex items-center gap-2 mt-1">
                <span className="kpi-label">{chart.chart_type}</span>
                {alarms.length > 0 && onShowEvents && (
                  <Badge
                    variant="outline"
                    className={
                      triggered
                        ? "border-destructive/30 text-destructive bg-destructive/10 cursor-pointer"
                        : "border-emerald-500/30 text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 cursor-pointer"
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowEvents?.(alarms[0], chart);
                    }}
                    data-testid={`chart-alarm-status-${chart.id}`}
                  >
                    <Bell className="h-3 w-3 mr-1" />
                    {alarms.length} alarm{alarms.length > 1 ? "s" : ""}
                    {triggered && <AlertTriangle className="h-3 w-3 ml-1" />}
                  </Badge>
                )}
                {yMode === "fixed" && (
                  <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                    Y fijo
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {canConfigure && (
              <Popover open={yPopoverOpen} onOpenChange={setYPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onMouseDown={(e) => e.stopPropagation()}
                    data-testid={`chart-yaxis-${chart.id}`}
                    aria-label="Eje Y"
                    title="Eje Y"
                  >
                    <Sliders className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  className="w-64 p-3"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="space-y-3">
                    <div className="text-xs font-semibold text-foreground">Eje Y</div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setYMode("auto")}
                        data-testid={`chart-y-auto-${chart.id}`}
                        className={`flex-1 h-8 rounded-md text-xs font-medium transition-colors ${
                          yMode === "auto"
                            ? "bg-primary text-primary-foreground"
                            : "border border-border hover:bg-accent"
                        }`}
                      >
                        Automático
                      </button>
                      <button
                        type="button"
                        onClick={() => setYMode("fixed")}
                        data-testid={`chart-y-fixed-${chart.id}`}
                        className={`flex-1 h-8 rounded-md text-xs font-medium transition-colors ${
                          yMode === "fixed"
                            ? "bg-primary text-primary-foreground"
                            : "border border-border hover:bg-accent"
                        }`}
                      >
                        Fijo
                      </button>
                    </div>
                    {yMode === "fixed" && (
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label htmlFor={`y-min-${chart.id}`} className="text-[10px] uppercase tracking-wide text-muted-foreground">
                            Y min
                          </Label>
                          <Input
                            id={`y-min-${chart.id}`}
                            type="number"
                            step="any"
                            value={yMin}
                            onChange={(e) => setYMin(e.target.value)}
                            placeholder="auto"
                            className="h-8"
                            data-testid={`chart-y-min-${chart.id}`}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor={`y-max-${chart.id}`} className="text-[10px] uppercase tracking-wide text-muted-foreground">
                            Y max
                          </Label>
                          <Input
                            id={`y-max-${chart.id}`}
                            type="number"
                            step="any"
                            value={yMax}
                            onChange={(e) => setYMax(e.target.value)}
                            placeholder="auto"
                            className="h-8"
                            data-testid={`chart-y-max-${chart.id}`}
                          />
                        </div>
                      </div>
                    )}
                    <Button
                      size="sm"
                      className="w-full"
                      onClick={saveYSettings}
                      disabled={savingY}
                      data-testid={`chart-y-save-${chart.id}`}
                    >
                      {savingY ? "Guardando…" : "Guardar"}
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onMouseDown={(e) => e.stopPropagation()}
                  data-testid={`chart-menu-${chart.id}`}
                  aria-label="Chart options"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={load} data-testid={`chart-refresh-${chart.id}`}>
                  <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                </DropdownMenuItem>
                {alarms.length > 0 && onShowEvents && (
                  <DropdownMenuItem
                    onClick={() => onShowEvents?.(alarms[0], chart)}
                    data-testid={`chart-history-${chart.id}`}
                  >
                    <History className="h-4 w-4 mr-2" /> Alarm history
                  </DropdownMenuItem>
                )}
                {canEdit && (
                  <>
                    <DropdownMenuItem onClick={() => onEdit?.(chart)} data-testid={`chart-edit-${chart.id}`}>
                      <Pencil className="h-4 w-4 mr-2" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onAddAlarm?.(chart)} data-testid={`chart-alarm-${chart.id}`}>
                      <Bell className="h-4 w-4 mr-2" /> Configure alarm
                    </DropdownMenuItem>
                    {!inGrid && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => bump(-2)}>
                          <Minimize2 className="h-4 w-4 mr-2" /> Make narrower
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => bump(2)}>
                          <Maximize2 className="h-4 w-4 mr-2" /> Make wider
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => bumpHeight(-40)}>
                          <Minimize2 className="h-4 w-4 mr-2" /> Make shorter
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => bumpHeight(40)}>
                          <Maximize2 className="h-4 w-4 mr-2" /> Make taller
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => onDelete?.(chart)}
                      data-testid={`chart-delete-${chart.id}`}
                    >
                      <Trash2 className="h-4 w-4 mr-2" /> Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent
          ref={containerRef}
          className={inGrid ? "pt-1 flex-1 min-h-0" : "pt-1"}
          style={inGrid ? { minHeight: 80 } : undefined}
        >
          {loading && rows.length === 0 ? (
            <div
              className="flex items-center justify-center text-xs text-muted-foreground"
              style={{ height: renderHeight }}
            >
              <span className="dot-pulse h-2 w-2 rounded-full bg-primary mr-2" /> Loading data…
            </div>
          ) : error ? (
            <div
              className="flex flex-col items-center justify-center text-center text-xs text-destructive"
              style={{ height: renderHeight }}
            >
              <AlertTriangle className="h-5 w-5 mb-2" />
              <p className="font-medium">Query failed</p>
              <p className="text-[11px] text-muted-foreground max-w-xs truncate">{error}</p>
            </div>
          ) : rows.length === 0 ? (
            <div
              className="flex items-center justify-center text-xs text-muted-foreground"
              style={{ height: renderHeight }}
            >
              No data returned by query.
            </div>
          ) : (
            <ChartRenderer
              type={chart.chart_type}
              rows={rows}
              xField={chart.x_field || "x"}
              yField={chart.y_field || "y"}
              xLabel={chart.x_label}
              yLabel={chart.y_label}
              color={chart.color || "#1A3556"}
              height={renderHeight}
              xRange={range}
              yMode={yMode}
              yMin={yMinNum}
              yMax={yMaxNum}
              thresholds={alarms.map((a) => ({
                value: a.threshold,
                label: `${a.condition === "gt" ? ">" : "<"} ${a.threshold}`,
                color: a.triggered ? "hsl(var(--destructive))" : "hsl(var(--warning))",
              }))}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
